import { CoordinatesProps } from '@/ui/components/Distance/Distance'
import { useCallback, useEffect, useEffectEvent, useState } from 'react'

const useGeolocation = (targetLocation?: CoordinatesProps) => {
  const [userLocation, setUserLocation] = useState<CoordinatesProps | null>(
    null
  )
  const [error, setError] = useState<string | null>(null)
  const [distance, setDistance] = useState<number | null>(null)

  const getUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords
          setUserLocation({ latitude, longitude })
        },
        (error) => {
          setError('Error getting user location: ' + error.message)
        }
      )
    } else {
      setError('Geolocation is not supported by this browser')
    }
  }, [])

  const calculateDistance = (
    loc1: CoordinatesProps,
    loc2: CoordinatesProps
  ): number => {
    const toRad = (value: number) => (value * Math.PI) / 180

    const lat1 = loc1.latitude
    const lon1 = loc1.longitude
    const lat2 = loc2.latitude
    const lon2 = loc2.longitude

    const R = 6371 // Radius of the Earth in kilometers
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const distance = R * c

    return distance // Distance in kilometers
  }

  const onGetUserLocation = useEffectEvent(() => {
    getUserLocation()
  })

  const onSetDistance = useEffectEvent((dist: number) => {
    setDistance(dist)
  })

  useEffect(() => {
    onGetUserLocation()
  }, [getUserLocation])

  useEffect(() => {
    if (userLocation && targetLocation) {
      const dist = calculateDistance(userLocation, targetLocation)
      onSetDistance(dist)
    }
  }, [userLocation, targetLocation])

  return { userLocation, error, distance, getUserLocation }
}

export default useGeolocation
